import React from 'react';
import type { DatasetData, OverlayMode, OverlaySelection } from './types';
import { OverlayModeSelector } from './OverlayModeSelector';
import { S3ImageDisplay } from './S3ImageDisplay';
import { colorForClassName, formatWeightLbs, parseWeightFromMetadata } from './bboxOverlay';

export const ImageDetailPanel: React.FC<{
  datasetData: DatasetData;
  overlay: OverlaySelection;
  onToggleOverlay: (mode: OverlayMode) => void;
  uniqueId: string;
  canvasRef: (el: HTMLCanvasElement | null) => void;
  imageRef: (el: HTMLImageElement | null) => void;
  onImageLoad: () => void;
  currentClassificationNames: string[];
  classificationInput: string;
  setClassificationInput: (v: string) => void;
  onAddClassification: () => void;
  onRemoveClassification: (name: string) => void;
  savingClassifications: boolean;
  hasWeights?: boolean;
  bboxClassNames: string[];
  visibleBboxClasses: Set<string>;
  onToggleBboxClass: (className: string) => void;
  onSetAllBboxClassesVisible: (visible: boolean) => void;
}> = ({
  datasetData,
  overlay,
  onToggleOverlay,
  uniqueId,
  canvasRef,
  imageRef,
  onImageLoad,
  currentClassificationNames,
  classificationInput,
  setClassificationInput,
  onAddClassification,
  onRemoveClassification,
  savingClassifications,
  hasWeights = false,
  bboxClassNames,
  visibleBboxClasses,
  onToggleBboxClass,
  onSetAllBboxClassesVisible,
}) => {
  const classificationLabels = (datasetData.ground_truth_labels || []).filter(
    (l) => l.type === 'classification' || (!l.bbox && l.class_name)
  );
  const primaryClassification = classificationLabels[0];
  const hasClassificationBanner = primaryClassification != null;
  const imageWeight = parseWeightFromMetadata(datasetData.metadata);
  const showWeightBanner = overlay.weights && imageWeight != null;
  const showClassToggles = overlay.bboxes && bboxClassNames.length > 0;
  const showClassificationBanner =
    hasClassificationBanner &&
    (overlay.classifications || overlay.bboxes || overlay.keypoints);

  return (
    <div className="dvc-results">
      <h3>Image Data</h3>
      <div className="dvc-prediction">
        <h4>Image: {datasetData.image_url}</h4>

        <OverlayModeSelector
          overlay={overlay}
          onToggle={onToggleOverlay}
          variant="detail"
          hasKeypoints={!!datasetData.keypoints?.length}
          hasWeights={hasWeights}
        />

        {showClassToggles && (
          <div className="dvc-bbox-class-toggles">
            <div className="dvc-bbox-class-toggles-header">
              <span>Show bounding box classes</span>
              <span className="dvc-bbox-class-toggles-actions">
                <button type="button" onClick={() => onSetAllBboxClassesVisible(true)}>
                  All
                </button>
                <button type="button" onClick={() => onSetAllBboxClassesVisible(false)}>
                  None
                </button>
              </span>
            </div>
            <div className="dvc-bbox-class-toggles-list">
              {bboxClassNames.map((name) => {
                const color = colorForClassName(name);
                return (
                  <label key={name} className="dvc-bbox-class-toggle">
                    <input
                      type="checkbox"
                      checked={visibleBboxClasses.has(name)}
                      onChange={() => onToggleBboxClass(name)}
                    />
                    <span
                      className="dvc-bbox-class-swatch"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {showWeightBanner && (
          <div
            className="dvc-weight-banner"
            style={{
              marginBottom: 16,
              padding: 16,
              background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
              color: 'white',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 18,
            }}
          >
            <span style={{ opacity: 0.9 }}>Weight:</span>{' '}
            <strong>{formatWeightLbs(imageWeight)}</strong>
          </div>
        )}

        {overlay.weights && imageWeight == null && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: '#fff3cd',
              color: '#856404',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 14,
            }}
          >
            No weight label for this image.
          </div>
        )}

        {showClassificationBanner && (
          <div
            className="dvc-classification-banner"
            style={{
              marginBottom: 16,
              padding: 16,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 18,
            }}
          >
            <span style={{ opacity: 0.9 }}>Classification:</span>{' '}
            <strong>{primaryClassification.class_name}</strong>
            {(primaryClassification.confidence != null ||
              primaryClassification.value != null) && (
              <span style={{ opacity: 0.95 }}>
                {' '}
                (
                {(
                  (primaryClassification.confidence ??
                    primaryClassification.value ??
                    1) * 100
                ).toFixed(1)}
                %)
              </span>
            )}
          </div>
        )}

        {datasetData.image_url && (
          <div className="dvc-image-preview">
            <h5>Image Preview with Ground Truth Labels</h5>
            <div className="dvc-image-container-wrapper" key={datasetData.image_url}>
              {datasetData.image_url.startsWith('s3://') ? (
                <S3ImageDisplay
                  key={datasetData.image_url}
                  s3Url={datasetData.image_url}
                  uniqueId={uniqueId}
                  canvasRef={canvasRef}
                  imageRef={imageRef}
                  onImageLoad={onImageLoad}
                />
              ) : (
                <img
                  src={datasetData.image_url}
                  alt="Dataset preview"
                  style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }}
                />
              )}
            </div>
          </div>
        )}

        {datasetData.ground_truth_labels && datasetData.ground_truth_labels.length > 0 && (
          <div className="dvc-ground-truth-section">
            <h5>Ground Truth Labels ({datasetData.ground_truth_labels.length})</h5>
            {datasetData.ground_truth_labels.map((gt, idx) => (
              <div key={idx} className="dvc-ground-truth-item">
                <strong>{gt.class_name}</strong>
                {gt.confidence != null && ` (${(gt.confidence * 100).toFixed(1)}%)`}
                {gt.bbox && (
                  <>
                    <br />
                    BBox: ({gt.bbox?.x1.toFixed(1)}, {gt.bbox?.y1.toFixed(1)}) to (
                    {gt.bbox?.x2.toFixed(1)}, {gt.bbox?.y2.toFixed(1)})
                  </>
                )}
                {gt.is_ground_truth && (
                  <>
                    <br />
                    <small>✓ Ground Truth from Dataset</small>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {datasetData.keypoints && datasetData.keypoints.length > 0 && (
          <div className="dvc-ground-truth-section">
            <h5>Keypoints ({datasetData.keypoints.length})</h5>
            {datasetData.keypoints.map((kp, idx) => (
              <div key={idx} className="dvc-ground-truth-item">
                <strong>{kp.category}</strong>
                <br />
                Position: ({kp.x}, {kp.y})
              </div>
            ))}
          </div>
        )}

        {overlay.classifications && (
          <div
            className="dvc-classification-edit"
            style={{
              marginTop: 16,
              padding: 12,
              background: '#f0f4ff',
              borderRadius: 8,
            }}
          >
            <h5 style={{ margin: '0 0 8px 0' }}>Classification labels</h5>
            <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#555' }}>
              Add or remove labels in the database only; the checked-out DVC CSV is not modified.
              The classification filter uses this merged view (CSV + DB).
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                value={classificationInput}
                onChange={(e) => setClassificationInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAddClassification()}
                placeholder="e.g. upright, vertical"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                }}
              />
              <button
                type="button"
                title="Writes to Postgres; does not edit data.csv on disk"
                onClick={onAddClassification}
                disabled={savingClassifications || !classificationInput.trim()}
                style={{
                  padding: '8px 16px',
                  background:
                    classificationInput.trim() && !savingClassifications ? '#667eea' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor:
                    classificationInput.trim() && !savingClassifications
                      ? 'pointer'
                      : 'not-allowed',
                }}
              >
                {savingClassifications ? 'Saving...' : 'Add to DB'}
              </button>
            </div>
            {currentClassificationNames.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {currentClassificationNames.map((name) => (
                  <span
                    key={name}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      marginRight: 8,
                      marginBottom: 6,
                      padding: '4px 8px',
                      background: '#e0e7ff',
                      borderRadius: 4,
                      fontSize: 14,
                    }}
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => onRemoveClassification(name)}
                      disabled={savingClassifications}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: savingClassifications ? 'not-allowed' : 'pointer',
                        padding: 0,
                        lineHeight: 1,
                        color: '#666',
                        fontSize: 16,
                      }}
                      aria-label={`Remove ${name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
